import { useState } from 'react'

/**
 * Rejoue `sync` quand `source` change d'identité — et seulement là.
 *
 * Le cas : un état local (formulaire contrôlé, sélection, statut) initialisé
 * depuis une donnée qui arrive plus tard, puis resynchronisé quand elle change.
 * Six écrans le faisaient avec un `useEffect` qui appelait `setState`, ce que
 * `react-hooks-extra/no-direct-set-state-in-use-effect` signale à juste titre :
 * un effet s'exécute APRÈS le rendu, donc React peint d'abord l'ancien état,
 * puis le remplace. Le formulaire s'affichait vide une image avant d'être
 * rempli — invisible en local, visible sur une machine lente.
 *
 * Mettre l'état à jour PENDANT le rendu est le remède documenté par React
 * (« adjusting state when a prop changes ») : React relance aussitôt le rendu
 * avec la nouvelle valeur, sans rien peindre entre les deux. Le coût est un
 * second passage de rendu, sur le seul composant concerné.
 *
 * Comparaison par identité, volontairement : les objets d'une réponse Apollo
 * gardent la même référence tant que le cache ne change pas, et en produisent
 * une nouvelle après un `refetch`. C'est exactement le signal qu'on veut —
 * comparer champ à champ écraserait une saisie en cours à chaque re-rendu.
 *
 * `sync` ne doit faire QUE des `setState` du composant appelant : tout le reste
 * (requête, navigation, écriture DOM) appartient à un effet.
 */
export function useSyncOnChange<T>(source: T | null | undefined, sync: (source: T) => void): void {
  const [synced, setSynced] = useState<T | null | undefined>(null)

  if (source != null && source !== synced) {
    setSynced(source)
    sync(source)
  }
}
